/* eslint-disable */
import { Alert, CircularProgress, Typography, Grid, Button, CardActions, Container, Card, CardContent, Paper } from '@mui/material';
import Box from '@mui/material/Box';
import { useState, useEffect, useRef } from 'react';
import Offer from '../Models/Offer';
import OfferMarket from '../Models/OfferMarket'
import { getMarketAndInfo, getCompanyOffers, handleUnauthorizedError } from '../Services/OfferService';
import Header from './Header';
import MarketDetails from './MarketDetails'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import OfferGridData from '../Models/OfferGridData';
import React from 'react';
import SnackbarMessage from "./SnackbarMessage";
import WithdrawOfferDialog from "./OfferWithdrawDialog";
import OfferFormDialog from "./OfferFormDialog";
import dayjs from 'dayjs';
import 'dayjs/locale/is';
import { MESSAGES } from '../Services/MessagesTexts';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import OfferSummary from './OfferSummary';
import StatusChip from './StatusChip';
import { FishYearCheckboxes, FishYearSelection } from './FishYearCheckboxes';
import { formatNumber, formatNumberFloat } from '../Services/FormatService';

function OfferHomePage() {

  // Use Icelandic locale
  dayjs.locale('is');
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [offerGridData, setOfferGridData] = useState<OfferGridData[]>([]);
  const [loadingMarketAndFishYears, setLoadingMarketAndFishYears] = useState(true);
  const [loadingCompanyOffers, setLoadingCompanyOffers] = useState(false);
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);
  const [buttonProps, setButtonProps] = useState({ disabled: true });
  const [isError, setError] = useState(false);
  const [isFormDialogOpen, setOpenFormDialog] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferGridData | null>(null);
  const [currentMarket, setCurrentMarket] = useState<OfferMarket>({} as OfferMarket);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [tonQuotaTypes, setTonQuotaTypes] = useState<number[]>([]);
  const initialSelectedYears = availableYears.reduce(
    (acc, year) => ({ ...acc, [year]: false }),
    { allFishYears: false }
  );
  const [selectedYears, setSelectedYears] = useState<Record<string, boolean>>(initialSelectedYears);
  const renderCount = useRef(0);
  const initialRender = useRef(true);

  useEffect(() => {
    const newSelectedYears = availableYears.reduce(
      (acc, year) => ({ ...acc, [year]: false }),
      {}
    );
    setSelectedYears(prevState => ({ ...prevState, ...newSelectedYears }));
  }, [availableYears]);


  const [snackbarData, setSnackbarData] = React.useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  } | null>(null);

  useEffect(() => {
    const initializeData = async () => {
      await fetchMarketAndFishYears();
    };
    initializeData();
  }, []);


  const fetchMarketAndFishYears = async (): Promise<void> => {
    setError(false);
    setLoadingMarketAndFishYears(true);
    try {
      let { currentMarket, fishYears, tonQuotaTypes: tonQuotaTypes } = await getMarketAndInfo();
      const isMarketClosed = currentMarket.id === null || currentMarket.id === undefined || currentMarket.id === 0;
      currentMarket.isClosed = isMarketClosed;
      setAvailableYears(fishYears);
      setCurrentMarket(currentMarket);
      setTonQuotaTypes(tonQuotaTypes);
    } catch (error) {
      setError(true);
      handleUnauthorizedError(error);
      setSnackbarOpen(true);
      setSnackbarData({ message: MESSAGES.FETCH_ERROR, type: 'error' });
    } finally {
      setLoadingMarketAndFishYears(false);
    }
  };

  useEffect(() => {
    if (initialRender.current) {
      // If it's the first render, just mark it as done and skip the rest of the effect.
      initialRender.current = false;
      return;
    }
    // If current market is closed disable the button.
    if (currentMarket && !currentMarket.isClosed) {
      setButtonProps({ disabled: false });
    } else {
      setButtonProps({ disabled: true });
    }
    fetchCompanyOffers([], false);
  }, [currentMarket]);

  /**
   * Fetches the company offers based on the selected years and `allFishYears` status. 
   * If no years are selected, and the `allFishYears` status is false, and the current market is empty, it fetches no offers.
   * The fetched offers are then sorted by date in descending order. 
   * These offers are transformed into a format suitable for the Offer Grid and then set to state.
   * If there's any error during the fetch, an error snackbar is shown.
   * 
   * @param selectedYears - Array of selected years.
   * @param allFishYears - Boolean indicating if all fish years are selected.
   * @returns Promise<void>
   */
  const fetchCompanyOffers = async (
    selectedYears: string[],
    allFishYears: boolean,
  ): Promise<void> => {
    setLoadingCompanyOffers(true);
    try {
      const emptyOfferList = selectedYears.length === 0 && !allFishYears && currentMarket.isClosed;
      let offers: Offer[] = [];
      if (!emptyOfferList) {
        offers = await getCompanyOffers(allFishYears, selectedYears);
      }
      const sortedOffers = offers.sort((a: Offer, b: Offer) => {
        return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
      });
      setOfferGridData(
        sortedOffers.map((offer: Offer) => ({
          id: offer.id,
          shipName: offer.ship.name,
          shipNumber: offer.ship.number,
          offerQuotaName: offer.offerQuota.name,
          offerQuotaQuantity: offer.offerQuota.quantity,
          paymentQuotaName: offer.paymentQuota.name,
          paymentQuotaQuantity: offer.paymentQuota.quantity,
          date: dayjs(offer.date).format('DD.MM.YYYY HH:mm'),
          status: offer.status,
          codValueRatio: offer.codValueRatio,
          valueCoefficient: offer.valueCoefficient,
          statusDescription: offer.statusDescription,
          isOpenMarket: offer.isOpenMarket,
          offerQuotaType: offer.offerQuota.quotaType,
          paymentQuotaType: offer.paymentQuota.quotaType,
        }))
      );
    } catch (error) {
      handleUnauthorizedError(error);
      setSnackbarOpen(true);
      setSnackbarData({ message: MESSAGES.FETCH_ERROR, type: 'error' });
    } finally {
      setLoadingCompanyOffers(false);
      setLoadingMarketAndFishYears(false);
    }
  };


  /**
   * Handles the actions after an offer has been withdrawn. 
   * It first displays a success snackbar notifying the user about the withdrawal of the offer. 
   * Next, it reconstructs the list of selected years (excluding the 'allFishYears' checkbox) 
   * and then fetches company offers again to reflect any changes due to the withdrawal. 
   * In case there's an error while fetching the offers, it sets the error state 
   * and displays an error snackbar.
   */
  const handleOfferWithdrawn = async () => {
    setSnackbarOpen(true);
    setSnackbarData({ message: MESSAGES.OFFER_WITHDRAWN, type: 'success' });

    // Extract the selected years and the special checkboxes' state
    const selectedYearList: string[] = Object.keys(selectedYears).filter(year => selectedYears[year] && year !== 'allFishYears');

    try {
      await fetchCompanyOffers(selectedYearList, selectedYears.allFishYearsState);
    } catch (error) {
      setError(true);
      setSnackbarOpen(true);
      setSnackbarData({ message: MESSAGES.FETCH_ERROR, type: 'error' });
    }
  };

  /**
   * Handles the actions after the create offer dialog has been closed. 
   * If an offer has been successfully submitted, it displays a success snackbar 
   * notifying the user about the new offer. 
   * Then, it reconstructs the list of selected years (excluding the 'allFishYears' checkbox) 
   * and fetches company offers again to reflect the addition of the new offer.
   * 
   * @param offerSubmitted - Boolean indicating if an offer was successfully submitted from the dialog.
   */
  const handleCloseCreateFormDialog = (offerSubmitted: boolean) => {
    setOpenFormDialog(false);
    if (offerSubmitted) {
      setSnackbarData({ message: 'Tilboð skráð', type: 'success' });
      setSnackbarOpen(true);
      const selectedYearList: string[] = Object.keys(selectedYears).filter(year => selectedYears[year] && year !== 'allFishYears');
      fetchCompanyOffers(selectedYearList, selectedYears.allFishYears);
    }
  };

  const handleCloseWithdrawDialog = (offerWithdrawn: boolean) => {
    setOpenWithdrawDialog(false);
    setSelectedOffer(null);
    if (offerWithdrawn) {
      handleOfferWithdrawn();
    }
  };

  function shouldDisplayWithdrawButton(offer: OfferGridData): boolean {
    return offer.status !== 0 && offer.isOpenMarket;
  }

  const closeSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleOpenFormDialog = () => {
    setOpenFormDialog(true);
  };

  /**
   * Watches for changes in the `selectedYears` state which represents the checkboxes' selection.
   * The function first maintains a count of renders. For the initial three renders, it skips 
   * the fetching of company offers to avoid unnecessary data fetching.
   * 
   * After the initial renders, it reconstructs the list of selected years (excluding the 'allFishYears' checkbox).
   * Then, it fetches the company offers to update the offer grid data based on the updated selection.
   * 
   * This ensures the displayed offers in the grid always match the user's checkbox selection.
   */
  useEffect(() => {
    // Increase the render count
    renderCount.current += 1;

    if (renderCount.current <= 3) {
      // Skip calling fetchCompanyOffers for the first three renders
      return;
    }
    const selectedYearList: string[] = Object.keys(selectedYears).filter(year => selectedYears[year] && year !== 'allFishYears');

    const selection: FishYearSelection = {
      selectedYears: selectedYearList,
      allFishYears: selectedYears.allFishYears
    };

    // Call fetchCompanyOffers with the selected parameters
    fetchCompanyOffers(selection.selectedYears, selection.allFishYears);

  }, [selectedYears]);

  const columns: GridColDef<OfferGridData>[] = [
    {
      field: 'action',
      headerName: 'Afturkalla',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!shouldDisplayWithdrawButton(params.row)) {
          return null;
        }

        return (
          <Button
            variant="outlined"
            color="error"
            size="small"
            tabIndex={params.hasFocus ? 0 : -1}
            onClick={() => {
              setSelectedOffer(params.row);
              setOpenWithdrawDialog(true);
            }}
            sx={{ minWidth: 'auto', minHeight: 36, px: 1.25, py: 0.5 }}
          >
            Afturkalla
          </Button>
        );
      },
    },
    {
      field: 'shipName',
      headerName: 'Skip',
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>Númer: {params.row.shipNumber}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={params.row.shipName}>{params.row.shipName}</Typography>
        </Box>
      )
    },
    { field: 'date', headerName: 'Dagsetning', width: 154 },
    {
      field: 'offerQuotaName',
      headerName: 'Óska eftir fisktegund',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>{params.row.offerQuotaName}</Typography>
            {params.row.offerQuotaQuantity != null && (
              <Typography variant="body2" color="text.secondary">Magn: {formatNumber(params.row.offerQuotaQuantity.toString())} {tonQuotaTypes.includes(params.row.offerQuotaType) ? 'tonn' : 'kg'}</Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'paymentQuotaName',
      headerName: 'Greiða með fisktegund',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>{params.row.paymentQuotaName}</Typography>
            {params.row.paymentQuotaQuantity != null && (
              <Typography variant="body2" color="text.secondary">Magn: {formatNumber(params.row.paymentQuotaQuantity.toString())} {tonQuotaTypes.includes(params.row.paymentQuotaType) ? 'tonn' : 'kg'}</Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'codValueRatio',
      headerName: 'Þorskígildihlutfall',
      width: 155,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const ratio = params.row.codValueRatio;
        return ratio == null ? '' : `${formatNumber(ratio.toString())}%`;
      }
    },
    {
      field: 'valueCoefficient',
      headerName: 'Virðisstuðull',
      width: 116,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        return formatNumberFloat(params.row.valueCoefficient?.toString() ?? '');
      }
    },
    {
      field: 'status',
      headerName: 'Staða',
      width: 182,
      renderCell: (params) => {
        const statusDescription = params.row.statusDescription ? params.row.statusDescription : (params.row.status === 1 ? "MÓTTEKIÐ" : "AFTURKALLAÐ");
        return <StatusChip status={statusDescription} />;
      },
    },
  ];


  const renderCard = (offer: OfferGridData) => (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
        <OfferSummary offer={offer} tonQuotaTypes={tonQuotaTypes} />
      </CardContent>
      <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <StatusChip status={offer.statusDescription || (offer.status === 1 ? 'Móttekið' : 'Afturkallað')} />
        {shouldDisplayWithdrawButton(offer) && (
          <Button variant="outlined" color="error" onClick={() => {
            setSelectedOffer(offer);
            setOpenWithdrawDialog(true);
          }}>Afturkalla</Button>
        )}
      </CardActions>
    </Card>
  );

  return (
    <>
      {openWithdrawDialog && selectedOffer?.id != null && (
        <WithdrawOfferDialog open={openWithdrawDialog} onClose={handleCloseWithdrawDialog} offer={selectedOffer} tonQuotaTypes={tonQuotaTypes} />
      )}
      <Header />
      {snackbarData && <SnackbarMessage message={snackbarData.message} type={snackbarData.type} open={snackbarOpen} handleClose={closeSnackbar} />}
      <Container component="main" maxWidth={false} sx={{ maxWidth: 1440, py: { xs: 3, md: 5 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Fiskistofa</Typography>
            <Typography component="h1" variant="h4">Tilboðsmarkaður</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenFormDialog} disabled={loadingMarketAndFishYears || isError || buttonProps.disabled}>
            Skrá Nýtt tilboð
          </Button>
        </Box>
        {!buttonProps.disabled && <OfferFormDialog open={isFormDialogOpen} onClose={handleCloseCreateFormDialog} tonQuotaTypes={tonQuotaTypes} />}
        {loadingMarketAndFishYears ? (
          <Paper variant="outlined" sx={{ display: 'flex', justifyContent: 'center', p: 8 }} role="status" aria-label="Sæki upplýsingar um markað">
            <CircularProgress aria-label="Sæki upplýsingar um markað" />
          </Paper>
        ) : isError ? (
          <Alert severity="error" action={<Button color="inherit" onClick={fetchMarketAndFishYears}>Reyna aftur</Button>}>
            {MESSAGES.FETCH_ERROR}
          </Alert>
        ) : (
          <>
            <MarketDetails offerMarket={currentMarket} />
            <Paper variant="outlined" component="section" aria-labelledby="offers-heading" sx={{ mt: 3, overflow: 'hidden' }}>
              <Box sx={{ p: { xs: 2.5, sm: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography id="offers-heading" variant="h6" component="h2" sx={{ mb: 1.5 }}>Tilboð</Typography>
                <FishYearCheckboxes years={availableYears} selectedYearsState={{ selectedYears, setSelectedYears }} isOpenMarketEmpty={currentMarket.isClosed} />
              </Box>
              {isLargeScreen ? (
                <Box sx={{ height: 560, minHeight: 360 }}>
                  <DataGrid
                    aria-label="Tilboð"
                    rows={offerGridData}
                    columns={columns}
                    rowHeight={80}
                    columnHeaderHeight={56}
                    loading={loadingCompanyOffers}
                    sx={{
                      border: 0, borderRadius: 0, fontSize: '0.875rem',
                      '& .MuiDataGrid-columnHeaders': { bgcolor: 'primary.light', color: 'text.primary', borderColor: 'divider' },
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
                      '& .MuiDataGrid-cell': { borderColor: 'divider', fontVariantNumeric: 'tabular-nums', px: 2 },
                      '& .MuiDataGrid-columnHeader': { px: 2 },
                      '& .MuiDataGrid-row:hover': { bgcolor: '#f7fafc' },
                      '& .MuiDataGrid-footerContainer': { borderColor: 'divider' },
                      '& .MuiDataGrid-cell:focus-visible, & .MuiDataGrid-columnHeader:focus-visible': { outline: '2px solid', outlineColor: 'secondary.main', outlineOffset: -2 },
                    }}
                    localeText={{
                      noRowsLabel: MESSAGES.NO_OFFERS_IN_MARKETPLACE,
                      MuiTablePagination: { labelRowsPerPage: MESSAGES.LINES_PER_PAGE },
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
                  {loadingCompanyOffers ? (
                    <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress aria-label="Sæki tilboð" />
                    </Box>
                  ) : offerGridData.length > 0 ? (
                    <Grid container spacing={2}>
                      {offerGridData.map(offer => <Grid item xs={12} md={6} key={offer.id}>{renderCard(offer)}</Grid>)}
                    </Grid>
                  ) : (
                    <Typography role="status" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>{MESSAGES.NO_OFFERS_IN_MARKETPLACE}</Typography>
                  )}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Container>
    </>
  );
}

export default OfferHomePage;
